import { ref } from 'vue';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Media } from '@capacitor-community/media';
import { Capacitor } from '@capacitor/core';

// Dimension max (plus grand côté) appliquée à toute photo VRF avant stockage/envoi :
// une photo mobile native (3000-4000px) n'apporte rien de plus qu'un rapport/écran
// à cette résolution, et son poids base64 domine largement le payload de soumission.
const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.8;

export function useCamera() {
  const photo = ref<string | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const requestPermissions = async () => {
    if (!Capacitor.isNativePlatform()) return true;
    const permissions = await Camera.requestPermissions({ permissions: ['camera', 'photos'] });
    return permissions.camera === 'granted' && permissions.photos !== 'denied';
  };

  const takePhoto = async () => {
    isLoading.value = true;
    error.value = null;
    try {
      const granted = await requestPermissions();
      if (!granted) {
        error.value = "Permission refusée. Veuillez autoriser l'accès à l'appareil photo.";
        return null;
      }
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        saveToGallery: false,
        width: MAX_DIMENSION,
      });
      photo.value = image.dataUrl ? await resizeDataUrl(image.dataUrl) : null;
      return photo.value;
    } catch (err: unknown) {
      if (err instanceof Error && err.message !== 'User cancelled photos app') {
        error.value = 'Impossible de prendre une photo.';
      }
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  // webPath (blob URL, retourné par pickImages) → data-URL base64 (format
  // uniforme utilisé partout ailleurs dans l'app pour stocker/soumettre une photo).
  const webPathToDataUrl = async (webPath: string): Promise<string> => {
    const blob = await (await fetch(webPath)).blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Redimensionne + recompresse une data-URL via canvas. `getPhoto` applique déjà
  // `width` nativement (no-op ici si l'image est déjà ≤ MAX_DIMENSION), mais
  // `pickImages` (galerie) n'a pas d'équivalent natif : ce passage canvas est donc
  // la seule étape de resize pour les photos de galerie, et une passe de
  // recompression JPEG homogène (poids) pour les deux sources.
  const resizeDataUrl = async (dataUrl: string): Promise<string> => {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = dataUrl;
    });

    const scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);

    const ctx = canvas.getContext('2d');
    if (!ctx) return dataUrl;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  };

  // Sélection multiple depuis la galerie (import groupé).
  const pickMultipleFromGallery = async (limit = 0) => {
    isLoading.value = true;
    error.value = null;
    try {
      const result = await Camera.pickImages({ limit, quality: 90 });
      const dataUrls = await Promise.all(result.photos.map((p) => webPathToDataUrl(p.webPath)));
      return await Promise.all(dataUrls.map(resizeDataUrl));
    } catch (err: unknown) {
      if (err instanceof Error && err.message !== 'User cancelled photos app') {
        error.value = "Impossible d'accéder à la galerie.";
      }
      return [];
    } finally {
      isLoading.value = false;
    }
  };

  const saveToGallery = async (dataUrl: string) => {
    isLoading.value = true;
    error.value = null;
    try {
      await Media.savePhoto({ path: dataUrl });
      return true;
    } catch {
      error.value = "Impossible d'enregistrer la photo dans la galerie.";
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const clearPhoto = () => {
    photo.value = null;
    error.value = null;
  };

  return {
    photo,
    isLoading,
    error,
    takePhoto,
    pickMultipleFromGallery,
    saveToGallery,
    clearPhoto,
  };
}
